import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import LocationSelector from '../components/LocationSelector';
import SaveProductButton from '../components/SaveProductButton';
import AddToBasketButton from '../components/AddToBasketButton';
import { useAuth } from '../context/AuthContext';
import { apiGet } from '../config/apiClient';

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl shadow-md p-4 animate-pulse">
      <div className="w-full h-28 bg-gray-200 rounded-lg mb-3"></div>
      <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
      <div className="h-5 bg-gray-200 rounded w-1/3 mx-auto mb-2"></div>
      <div className="h-3 bg-gray-100 rounded w-1/2 mx-auto mb-3"></div>
      <div className="h-8 bg-gray-200 rounded w-full"></div>
    </div>
  );
}

function UnlockScreen({ type }) {
  const navigate = useNavigate();

  if (type === 'auth_required') {
    return (
      <div className="max-w-lg mx-auto text-center py-16 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-50 rounded-full mb-6">
            <svg className="w-10 h-10 text-red-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Search and Compare Prices</h2>
          <p className="text-gray-600 mb-2">
            Create a free account to compare prices across stores and find the best deals.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-700 mb-2">How it works:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">1.</span>
                <span>Sign up and start searching instantly</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">2.</span>
                <span>Compare prices across your local stores</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">3.</span>
                <span>Share a receipt to keep searching</span>
              </li>
            </ul>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition font-semibold"
            >
              Sign Up Free
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 border-2 border-red-800 text-red-800 rounded-lg hover:bg-red-50 transition font-semibold"
            >
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto text-center py-16 px-4">
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-50 rounded-full mb-6">
          <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Keep Searching</h2>
        <p className="text-gray-600 mb-2">
          Share a receipt from any store to keep comparing prices.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Every receipt you upload helps the community and unlocks more searches for you.
        </p>
        <button
          onClick={() => navigate('/upload')}
          className="px-8 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition font-semibold text-lg"
        >
          Upload a Receipt
        </button>
      </div>
    </div>
  );
}

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const routeLocation = useLocation();
  const searchQueryFromUrl = searchParams.get('q') || '';

  const [searchTerm, setSearchTerm] = useState(searchQueryFromUrl);
  const [inputValue, setInputValue] = useState(searchQueryFromUrl);
  const [location, setLocation] = useState(() => ({
    country: { id: searchParams.get('country_id') || '', name: searchParams.get('country_name') || '' },
    region: { id: searchParams.get('region_id') || '', name: searchParams.get('region_name') || '' },
    city: { id: searchParams.get('city_id') || '', name: searchParams.get('city_name') || '' },
    neighborhood: { id: searchParams.get('neighborhood_id') || '', name: searchParams.get('neighborhood_name') || '' },
  }));
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchMessage, setSearchMessage] = useState('');
  const [hasSearched, setHasSearched] = useState(!!searchQueryFromUrl);
  const [gateType, setGateType] = useState(null);
  const { authToken, user, isAuthenticated, points, refreshPoints, authLoading } = useAuth();
  const [pagination, setPagination] = useState(null);
  const [showWelcome, setShowWelcome] = useState(routeLocation.state?.welcome || false);
  const [countryAutoSet, setCountryAutoSet] = useState(false);
  const [popularProducts, setPopularProducts] = useState([]);
  const [loadingPopular, setLoadingPopular] = useState(false);

  const currentPageFromParams = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    if (countryAutoSet || !user?.country_slug || searchParams.get('country_id')) return;
    apiGet('/locations/countries')
      .then(data => {
        const countries = Array.isArray(data) ? data : data.countries || [];
        const match = countries.find(c => c.slug === user.country_slug);
        if (match) {
          const countryLoc = { id: match.id.toString(), name: match.name };
          setLocation(prev => ({ ...prev, country: countryLoc }));
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('country_id', match.id.toString());
            next.set('country_name', match.name);
            next.set('page', '1');
            return next;
          });
        }
        setCountryAutoSet(true);
      })
      .catch(() => setCountryAutoSet(true));
  }, [user, countryAutoSet, searchParams, setSearchParams]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setGateType('auth_required');
    } else if (points !== null && points <= 0) {
      setGateType('insufficient_points');
    } else {
      setGateType(null);
    }
  }, [isAuthenticated, points, authLoading]);

  const fetchResults = useCallback(async () => {
    if (gateType) return;

    if (!searchTerm) {
      setResults([]);
      if (!location.country.id) {
        setSearchMessage('Please enter a search term or select a location to start.');
      }
      return;
    }

    setHasSearched(true);
    setLoading(true);
    setError('');
    setSearchMessage('');

    const queryParams = new URLSearchParams();
    if (searchTerm) queryParams.append('q', searchTerm);
    if (location.country.id) queryParams.append('country_id', location.country.id);
    if (location.region.id) queryParams.append('region_id', location.region.id);
    if (location.city.id) queryParams.append('city_id', location.city.id);
    if (location.neighborhood.id) queryParams.append('neighborhood_id', location.neighborhood.id);
    queryParams.append('page', currentPageFromParams.toString());

    try {
      const data = await apiGet(`/products/search?${queryParams.toString()}`);
      setResults(data.data || []);
      setPagination({ current_page: data.current_page, last_page: data.last_page, ...data });
      if (data.remaining_points !== undefined) {
        refreshPoints();
      }
      if ((data.data && data.data.length === 0) || !data.data) {
        setSearchMessage(data.message || 'No products found matching your criteria.');
      } else {
        setSearchMessage('');
      }
    } catch (err) {
      console.error('Search error:', err);
      if (err.status === 401) {
        setGateType('auth_required');
      } else if (err.status === 403 && err.data?.error_type === 'insufficient_points') {
        setGateType('insufficient_points');
      } else {
        setError(err.data?.message || err.message || 'Failed to connect. Check your connection and try again.');
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, location, authToken, currentPageFromParams, gateType, refreshPoints]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    setSearchTerm(searchQueryFromUrl);
    setInputValue(searchQueryFromUrl);
  }, [searchQueryFromUrl]);

  useEffect(() => {
    if (!isAuthenticated || hasSearched) return;
    setLoadingPopular(true);
    apiGet('/products/popular?limit=8')
      .then(data => {
        const items = Array.isArray(data) ? data : data.data || [];
        setPopularProducts(items);
      })
      .catch(() => setPopularProducts([]))
      .finally(() => setLoadingPopular(false));
  }, [isAuthenticated, hasSearched]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (trimmed) {
        next.set('q', trimmed);
      } else {
        next.delete('q');
      }
      next.set('page', '1');
      return next;
    });
  };

  const handleLocationChange = (selectedLocation) => {
    setLocation(selectedLocation);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      ['country', 'region', 'city', 'neighborhood'].forEach(key => {
        if (selectedLocation[key]?.id) {
          next.set(`${key}_id`, selectedLocation[key].id);
          next.set(`${key}_name`, selectedLocation[key].name);
        } else {
          next.delete(`${key}_id`);
          next.delete(`${key}_name`);
        }
      });
      next.set('page', '1');
      return next;
    });
  };

  const handleProductSaveStatusChange = (productId, newSavedStatus) => {
    setResults(prevResults => 
      prevResults.map(product => 
        product.id === productId ? { ...product, is_saved_by_user: newSavedStatus } : product
      )
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && pagination && newPage <= pagination.last_page) {
      setSearchParams(prev => { 
        const next = new URLSearchParams(prev); 
        next.set('page', newPage.toString()); 
        return next; 
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const locationLabel = [location.city.name, location.region.name, location.country.name]
    .filter(Boolean).join(', ');

  const paginationBtnClass = "px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition min-w-[44px] min-h-[44px] flex items-center justify-center";

  if (authLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-20 text-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-red-800 mx-auto"></div>
        <p className="text-sm text-gray-500 mt-3">Loading...</p>
      </div>
    );
  }

  if (gateType) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10 pb-24 md:pb-10">
        <UnlockScreen type={gateType} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10 pb-24 md:pb-10">
      {showWelcome && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-green-800">Welcome to BoVi Price!</h3>
            <p className="text-sm text-green-700 mt-1">Search for any product to compare prices across stores.</p>
          </div>
          <button onClick={() => setShowWelcome(false)} className="text-green-500 hover:text-green-700 ml-4 flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            {searchTerm ? `Results for "${searchTerm}"` : 'Search Products'}
          </h1>
          {points !== null && (
            <span className="text-sm text-amber-700 font-semibold bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
              {points} pts
            </span>
          )}
        </div>
        {locationLabel && (
          <p className="text-sm text-gray-500 mt-1">Filtering near {locationLabel}</p>
        )}
      </div>

      <form onSubmit={handleSearchSubmit} className="mb-6 flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Search for products, brands, or stores..."
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent"
        />
        <button
          type="submit"
          className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition text-sm font-medium whitespace-nowrap"
        >
          Search
        </button>
      </form>

      <details className="mb-6 bg-white border rounded-lg shadow-sm">
        <summary className="cursor-pointer px-4 py-3 font-medium text-gray-700 hover:bg-gray-50 rounded-lg flex items-center justify-between">
          <span>Filter by Location</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </summary>
        <div className="px-4 pb-4">
          <LocationSelector onChange={handleLocationChange} initialLocation={location} />
        </div>
      </details>

      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {error && (
        <div className="text-center py-12 px-4">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Something went wrong</h3>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <button onClick={fetchResults} className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition text-sm font-medium">
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && results.length === 0 && !hasSearched && (
        <div className="py-6 px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4">
              <svg className="w-8 h-8 text-red-800" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">What are you looking for?</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">Tap a suggestion below or type your own search above.</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
              {['Rice', 'Chicken', 'Bread', 'Sugar', 'Oil', 'Milk', 'Flour', 'Eggs', 'Butter', 'Water'].map(term => (
                <button
                  key={term}
                  onClick={() => {
                    setInputValue(term);
                    setSearchParams(prev => {
                      const next = new URLSearchParams(prev);
                      next.set('q', term);
                      next.set('page', '1');
                      return next;
                    });
                  }}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-800 transition shadow-sm"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>

          {loadingPopular && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {!loadingPopular && popularProducts.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Popular Products</h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {popularProducts.map(product => (
                  <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col hover:shadow-md transition duration-200">
                    <Link
                      to={`/product/${product.id}`}
                      className="flex flex-col items-center p-3 sm:p-4 flex-grow"
                    >
                      <img
                        src={product.image_url || product.img || '/placeholder-product.svg'}
                        alt={product.name}
                        className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-lg mb-3"
                        loading="lazy"
                      />
                      <h3 className="font-semibold text-gray-800 text-center text-sm leading-tight mb-1 line-clamp-2">{product.name}</h3>
                      {product.brand?.name && (
                        <span className="text-xs text-gray-400 mb-1">{product.brand.name}</span>
                      )}
                      {product.price && (
                        <div className="text-red-800 font-bold text-lg">${parseFloat(product.price).toFixed(2)}</div>
                      )}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !error && results.length === 0 && hasSearched && searchMessage && (
        <div className="text-center py-16 px-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No products found</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">{searchMessage}</p>
          {locationLabel && (
            <button 
              onClick={() => {
                const empty = { country: { id: '', name: '' }, region: { id: '', name: '' }, city: { id: '', name: '' }, neighborhood: { id: '', name: '' } };
                setLocation(empty);
                setSearchParams(prev => {
                  const next = new URLSearchParams(prev);
                  ['country', 'region', 'city', 'neighborhood'].forEach(key => {
                    next.delete(`${key}_id`);
                    next.delete(`${key}_name`);
                  });
                  next.set('page', '1');
                  return next;
                });
              }}
              className="mt-4 text-sm text-red-800 hover:text-red-900 underline"
            >
              Clear location filter
            </button>
          )}
        </div>
      )}

      {!loading && !error && results.length > 0 && (
        <>
          {pagination && (
            <p className="text-xs text-gray-500 mb-3">
              {pagination.total || 0} result{(pagination.total || 0) !== 1 ? 's' : ''} found
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {results.map((product) => (
              <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col hover:shadow-md transition duration-200">
                <Link 
                  to={`/product/${product.id}`} 
                  className="flex flex-col items-center p-3 sm:p-4 flex-grow"
                >
                  <img 
                    src={product.image_url || product.img || '/placeholder-product.svg'}
                    alt={product.name}
                    className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-lg mb-3"
                    loading="lazy"
                  />
                  <h3 className="font-semibold text-gray-800 text-center text-sm leading-tight mb-1 line-clamp-2">{product.name}</h3>
                  {product.brand?.name && (
                    <span className="text-xs text-gray-400 mb-1">{product.brand.name}</span>
                  )}
                  {product.price && (
                    <div className="text-red-800 font-bold text-lg">${parseFloat(product.price).toFixed(2)}</div>
                  )}
                  {product.store_name && (
                    <span className="text-xs text-gray-500 mt-0.5">at {product.store_name}</span>
                  )}
                  {product.updated_at && (
                    <span className="text-[10px] text-gray-400 mt-1">
                      Updated {new Date(product.updated_at).toLocaleDateString()}
                    </span>
                  )}
                </Link>
                <div className="px-3 pb-3 sm:px-4 sm:pb-4">
                  <SaveProductButton 
                    productId={product.id} 
                    initialIsSaved={product.is_saved_by_user || false} 
                    onSaveStatusChange={handleProductSaveStatusChange}
                  />
                  <AddToBasketButton productId={product.id} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && !error && results.length > 0 && pagination && pagination.last_page > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {pagination.from || 0}–{pagination.to || 0} of {pagination.total || 0}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-center">
            <button 
              onClick={() => handlePageChange(1)} 
              disabled={pagination.current_page === 1} 
              className={paginationBtnClass}
              aria-label="First page"
            >First</button>
            <button 
              onClick={() => handlePageChange(pagination.current_page - 1)} 
              disabled={!pagination.prev_page_url} 
              className={paginationBtnClass}
              aria-label="Previous page"
            >&laquo;</button>
            <span className="text-sm text-gray-700 px-2">
              Page <strong>{pagination.current_page}</strong> of {pagination.last_page}
            </span>
            <button 
              onClick={() => handlePageChange(pagination.current_page + 1)} 
              disabled={!pagination.next_page_url} 
              className={paginationBtnClass}
              aria-label="Next page"
            >&raquo;</button>
            <button 
              onClick={() => handlePageChange(pagination.last_page)} 
              disabled={pagination.current_page === pagination.last_page} 
              className={paginationBtnClass}
              aria-label="Last page"
            >Last</button>
          </div>
        </div>
      )}
    </div>
  );
}
